import * as React from "react";
import {
  Box,
  Button,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import makeStyles from "@mui/styles/makeStyles";
import LeavingFDICDialog from "../LeavingFDICDialog";
import { ApiBaseUrl, LoginGovBaseUrl, LoginGovClientId } from "../../config";
import { fetchData } from "../../common/services/ApiClient";
import {
  removeUrlParameter,
  deleteCookiebyName,
  getCookiebyName,
} from "../../common/services/Utilities";
import { KeyValue } from "../../interfaces";
import { v4 as uuid } from "uuid";

const useClasses = makeStyles(() => ({
  loginPanel: {
    "textAlign": "center",
    "& #activeUserEmail": {
      wordBreak: "break-word",
    },
  },
  header: {
    paddingBottom: 0,
  },
  loginList: {
    "margin": "0 0 2rem 2rem",
    "& li": { fontSize: "1rem", display: "list-item", listStyleType: "disc" },
  },
  continueLabel: {
    "textAlign": "center",
    "& > h2": {
      fontSize: 24,
    },
  },
}));

interface Props {
  updateUUID: (uuid: string) => void;
  closeRedirect?: string;
  label?: string;
  formButton?: boolean;
  noDisplay?: boolean;
  banner: string;
}

export default function LoginPanel({
  updateUUID,
  closeRedirect,
  label,
  formButton,
  noDisplay,
  banner,
}: Props) {
  const classes = useClasses();
  const history = useHistory();
  const urlSearch =
    history && history.location && history.location.search
      ? history.location.search
      : "";
  const logoutUrlSearch = urlSearch
    ? `${urlSearch}&logout=true`
    : "?logout=true";
  const urlString = new URLSearchParams(urlSearch).toString();
  const userStorageValues = React.useMemo(
    () => ["uuid", "email", "id_token"],
    [],
  );
  const [userInfo, setUserInfo] = React.useState({
    uuid: "",
    email: "",
    id_token: "",
  });
  localStorage.getItem("nonce")
    ? localStorage.getItem("nonce")
    : localStorage.setItem("nonce", uuid());

  React.useEffect(() => {
    const localStorageObj = userStorageValues
      .map((item: string) => {
        return { key: item, value: getCookiebyName(item) };
      })
      .reduce(
        (obj: KeyValue, item: KeyValue) =>
          Object.assign(obj, { [item.key]: item.value }),
        {},
      );
    if (
      !Object.values(localStorageObj).includes(null) &&
      userInfo.uuid === ""
    ) {
      setUserInfo({
        uuid: localStorageObj.uuid as string,
        email: localStorageObj.email as string,
        id_token: localStorageObj.id_token as string,
      });

      updateUUID(localStorageObj.uuid as string);
    }
  }, [updateUUID, userInfo.uuid, userStorageValues, userInfo.id_token]);

  React.useEffect(() => {
    if (userInfo.email === "" && urlString.includes("code")) {
      updateUUID("LOADING");
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/auth?code=${new URLSearchParams(urlSearch).get(
            "code",
          )}&nonce=${localStorage.getItem("nonce")}`,
        )
          .then(response => {
            userStorageValues.forEach((value: string) => {
              document.cookie = `${value}=${response.data[value]};HttpOly;Secure;`;
            });
            setUserInfo({
              uuid: response.data.uuid,
              email: response.data.email,
              id_token: response.data.id_token,
            });
            updateUUID(response.data.uuid);
            localStorage.removeItem("nonce");
          })
          .catch(error => {
            console.error(error);
            updateUUID("");
          });
      })();
    }
  }, [updateUUID, urlSearch, urlString, userInfo.email, userStorageValues]);

  React.useEffect(() => {
    if (
      urlString.includes("state") &&
      !urlString.includes("code") &&
      localStorage.length === 0
    ) {
      history.push(removeUrlParameter(window.location.href, ["state"]));
    } else if (urlString.includes("code")) {
      history.push(removeUrlParameter(window.location.href, ["state", "code"]));
    }
  }, [history, urlString]);

  const loginContent = (
    <>
      <Typography variant="body2">
        We are using Login.gov to secure your customized content. Login.gov is
        an official product of the GSA that partners with other Federal agencies
        as well as State and Local entities.
      </Typography>
      <List dense className={classes.loginList}>
        <ListItem disableGutters>Login.gov is FREE to use</ListItem>
        <ListItem disableGutters>
          A single account can link you to other participating sites
        </ListItem>
        <ListItem disableGutters>
          It’s very secure and no private information will be shared
        </ListItem>
        <ListItem disableGutters>
          If you already use Login.gov, all you need to do is sign in with your
          current username/password
        </ListItem>
      </List>
      <DialogTitle className={classes.continueLabel}>
        Do you wish to continue?
      </DialogTitle>
    </>
  );
  const dialog = (
    <LeavingFDICDialog
      webAddress={`${LoginGovBaseUrl()}/openid_connect/authorize?acr_values=http://idmanagement.gov/ns/assurance/ial/1&client_id=${LoginGovClientId()}&nonce=${localStorage.getItem(
        "nonce",
      )}&prompt=select_account&redirect_uri=${
        window.location.origin
      }/bankfind-suite&response_type=code&scope=openid+email&state=${localStorage.getItem(
        "nonce",
      )}${encodeURIComponent(window.location.pathname + urlSearch)}`}
      label={label ? label : "Account Access"}
      noHover
      content={loginContent}
      sameWindow
      button
      closeRedirect={closeRedirect}
    />
  );
  return noDisplay || banner === "true" ? (
    <></>
  ) : closeRedirect || formButton ? (
    dialog
  ) : (
    <Box className={classes.loginPanel}>
      {!userInfo.email ? (
        <>
          <Typography className={classes.header} variant="h3">
            Personal Account Access
          </Typography>
          <Typography variant="body2">
            Create and save your customized content for future use or with other
            BankFind Suite functionality.
          </Typography>
          <Typography id="account-access-button" component="div">
            {dialog}
          </Typography>
        </>
      ) : (
        <>
          <Typography className={classes.header} variant="h3">
            Active user:
          </Typography>
          <Typography id="activeUserEmail" variant="body2" color="primary">
            {userInfo.email}
          </Typography>
          <Button
            id="signOut"
            fullWidth
            color="secondary"
            variant="contained"
            href={`${LoginGovBaseUrl()}/openid_connect/logout?client_id=${LoginGovClientId()}&post_logout_redirect_uri=${
              window.location.origin
            }/bankfind-suite&state=${localStorage.getItem(
              "nonce",
            )}${encodeURIComponent(
              window.location.pathname + logoutUrlSearch,
            )}`}
            onClick={() => {
              setUserInfo({
                uuid: "",
                email: "",
                id_token: "",
              });
              localStorage.clear();
              deleteCookiebyName("id_token");
              deleteCookiebyName("email");
              deleteCookiebyName("uuid");
            }}
          >
            Sign-Out
          </Button>
        </>
      )}
    </Box>
  );
}
